import { DefaultBlogImage } from 'components/_common/Icons';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import GunForSaleCondition from 'utils/enum/gun_for_sale_condition';
import ProductCatalogType from 'utils/enum/product_catalog_type';

import { getFullName, placeParamsToURL } from './CommonHelper';
import { getImageURL, IMAGE_SIZE } from './FileUploadHelper';
export const REPLY_MESSAGE_LINK =
  'https://mail.google.com/mail/?view=cm&ui=2&tf=0&fs=1&to=contact%40mylocalgunshop.com';
// catgegory slugs
export const RIFLES_CATEGORY_SLUG = 'rifles';
export const HANDGUNS_CATEGORY_SLUG = 'handguns';
export const SHOTGUNS_CATEGORY_SLUG = 'shotguns';
export const SILENCERS_CATEGORY_SLUG = 'silencers';
export const AMMUNITION_CATEGORY_SLUG = 'ammunition';
export const MERCHANDISE_CATEGORY_SLUG = 'merchandise';
export const ACCESSORIES_AND_OPTICS_CATEGORY_SLUG = 'accessories-optics';
//
export const CUSTOMER_SLUG = 'customer';
export const GENERAL_SETTING_SLUG = 'general_setting';
export const CUSTOMER_GROUP_SLUG = 'customer_group';
export const EMPLOYEE_SLUG = 'employee';
export const ROLE_SLUG = 'role';
export const FFL_SLUG = 'ffl';
export const ORDER_SLUG = 'order';
export const ORDER_DETAIL_SLUG = 'order_detail';
export const PURCHASE_FORMS_SLUG = 'purchase_forms';
export const REVIEW_SLUG = 'review';
export const PRODUCT_REVIEW_SLUG = 'product_review_slug';
export const MESSAGE_SLUG = 'message';
export const AFFILIATE_CODE_SLUG = 'affiliate_code';
export const AFFILIATE_ANALYSIS_SLUG = 'affiliate_analytics';
export const BLOG_SLUG = 'blog';
export const BLOG_CATEGORY_SLUG = 'blog_category';
export const CATEGORY_SLUG = 'category';
export const MANUFACTURER_SLUG = 'manufacturer';
export const BANNER_SLUG = 'banner';
// home page hero slider shows at most this many banners
export const MAX_ACTIVE_BANNERS = 5;
export const URL_REWRITE_SLUG = 'url_rewrite';
export const PRODUCT_SLUG = 'product';
// Admin Products page tabs - which products each tab lists, sent to the API as `catalog_scope`.
// Ammo, Silencers and Merchandise list their own product catalog type; Firearms ("product") is
// everything else.
export const PRODUCT_CATALOG_SCOPE = {
  product: 'product', // Firearms tab: not merchandise, ammunition or silencers
  productWithMerchandise: 'product_with_merchandise', // Firearms tab for users with no Merchandise tab
  ammo: 'ammo',
  silencers: 'silencers',
  merchandise: 'merchandise',
} as const;
export type ProductCatalogScope =
  (typeof PRODUCT_CATALOG_SCOPE)[keyof typeof PRODUCT_CATALOG_SCOPE];
// PageHeader count key and export file name per tab
export const PRODUCT_SCOPE_DETAILS: Record<
  ProductCatalogScope,
  { slug: string; fileName: string }
> = {
  product: { slug: PRODUCT_SLUG, fileName: 'product_list' },
  product_with_merchandise: { slug: PRODUCT_SLUG, fileName: 'product_list' },
  ammo: { slug: 'ammo_list', fileName: 'ammo_list' },
  silencers: { slug: 'silencers_list', fileName: 'silencers_list' },
  merchandise: { slug: 'merchandise_list', fileName: 'merchandise_list' },
};
// the catalog type each of its own tabs lists - the tab's "Add" preselects it
export const PRODUCT_SCOPE_CATALOG_TYPE: Partial<Record<ProductCatalogScope, string>> = {
  ammo: ProductCatalogType.Ammunition,
  silencers: ProductCatalogType.Silencers,
  merchandise: ProductCatalogType.Merchandise,
};
// the tab of a catalog type that has its own tab (Ammunition / Silencers / Merchandise)
export const getCatalogScopeByType = (catalogType?: string | null) =>
  (Object.keys(PRODUCT_SCOPE_CATALOG_TYPE) as ProductCatalogScope[]).find(
    (scope) => PRODUCT_SCOPE_CATALOG_TYPE[scope] === catalogType
  );
// the top-level category a product of a tab's own type goes in - locked in the product form
export const CATALOG_TYPE_CATEGORY_SLUG: Record<string, string> = {
  [ProductCatalogType.Ammunition]: AMMUNITION_CATEGORY_SLUG,
  [ProductCatalogType.Silencers]: SILENCERS_CATEGORY_SLUG,
  [ProductCatalogType.Merchandise]: MERCHANDISE_CATEGORY_SLUG,
};
// add-product URL flag (?type=ammo|silencers|merchandise) that preselects the tab's product type
export const PRODUCT_ADD_TYPE_PARAM = 'type';
export const MARKETPLACE_SLUG = 'marketplace';
export const PRODUCT_ATTRIBUTE_SLUG = 'product_attribute';
// Dynamic/Fixed Attributes are now separate top-level tabs (own routes, own
// PageHeader count) - distinct from PRODUCT_ATTRIBUTE_SLUG, which stays the shared
// key for table settings/export (both tabs use the same columns/export config).
export const PRODUCT_ATTRIBUTE_DYNAMIC_SLUG = 'product_attribute_dynamic';
export const PRODUCT_ATTRIBUTE_FIXED_SLUG = 'product_attribute_fixed';
export const ADVERTISMENT_SLUG = 'advertisement';
export const ADVERTISMENT_LOCATION_SLUG = 'advertisement_location';
export const ACTIVE_GUNS_FOR_SALE_SLUG = 'active_guns_for_sale';
export const SOLD_GUNS_FOR_SALE_SLUG = 'sold_guns_for_sale';
export const GUNS_FOR_SALE_SLUG = 'guns_for_sale';
export const COUPON_SLUG = 'coupon';
export const EMAIL_TEMPLATE_SLUG = 'email_template';
export const SMS_TEMPLATE_SLUG = 'sms';
export const SUBSCRIPTION_PLAN_SLUG = 'subscription_plan';
export const CONTENT_PAGE_SLUG = 'content_page';
export const FAQ_SLUG = 'faq';
export const DEFAULT_MOBILE_CODE = '1';
export const DEFAULT_CURRENCY_SYMBOL = '$';
export const PERCENTAGE_SYMBOL = '%';
export const MOBILE_CODE_LABEL_VAL = `+${DEFAULT_MOBILE_CODE}`;
export const LOG_TYPE_VIEW_SLUG = 'view';
export const TRANSFER_REQUEST_SLUG = 'transfer_request';
export const TR_COMPLETED_STATUS_SLUG = 'completed';

export const RETURNABLE_ORDER_ITEM_STATUSES = [
  // 'pending',
  'shipped',
  'completed',
  // 'paid',
  // 'transfer_request',
  // 'ready_to_shipment',
  // 'ready_to_pickup',
];

export const isReturnableOrderItemStatus = (status: any): boolean => {
  const s = String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]/g, '_');
  return RETURNABLE_ORDER_ITEM_STATUSES.includes(s);
};

// An order item the customer can ask to return: a returnable status, and the seller takes the
// product back (the API's allow_return_refund: the product's setting, else its FFL's)
export const isOrderItemReturnable = (item: any): boolean =>
  isReturnableOrderItemStatus(item?.status) && item?.allow_return_refund !== false;

// The coupon codes a checkout calculation applied - several when the seller allows stacking them
// (`coupons`), else the single `coupon` older responses carry
export const getCalculationCouponCodes = (calculation: any): string[] => {
  const coupons = Array.isArray(calculation?.coupons) ? calculation.coupons : [];
  const codes = coupons.map((coupon: any) => coupon?.code).filter(Boolean);
  if (codes.length > 0) return codes;
  return calculation?.coupon?.code ? [calculation.coupon.code] : [];
};

export const RETURN_REQUEST_SLUG = 'return_request';

// customer order listing filters (matched on order item return/refund statuses by the API)
export const RETURN_FILTER_SLUG = 'return';
export const REFUND_FILTER_SLUG = 'refund';

export const RETURN_REQUEST_STATUS_PENDING = 'pending';
export const RETURN_REQUEST_STATUS_APPROVED = 'approved';
export const RETURN_REQUEST_STATUS_REJECTED = 'rejected';
export const RETURN_REQUEST_STATUS_COMPLETED = 'completed';

export const getReturnRequestStatusOptions = (t: any) => [
  { slug: '', name: t('all') },
  { slug: RETURN_REQUEST_STATUS_PENDING, name: t('pending') },
  { slug: RETURN_REQUEST_STATUS_APPROVED, name: t('approved') },
  { slug: RETURN_REQUEST_STATUS_REJECTED, name: t('rejected') },
  { slug: RETURN_REQUEST_STATUS_COMPLETED, name: t('completed') },
];

export const getReturnLifecycleStatusOptions = (t: any) => [
  { slug: 'return_completed', name: t('return_completed') },
  { slug: 'refund_in_progress', name: t('refund_in_progress') },
  { slug: 'refund_complete', name: t('refund_complete') },
];

const getOrderItemRefundValue = (item: any) => {
  if (item?.refund_amount !== undefined && item?.refund_amount !== null) {
    return Number(item.refund_amount) || 0;
  }
  if (item?.total_amount !== undefined && item?.total_amount !== null) {
    return Number(item.total_amount) || 0;
  }
  return (Number(item?.unit_price) || 0) * (Number(item?.quantity) || 1);
};

// item is going back to the vendor (return_rejected excluded - the item stays with the customer)
export const RETURN_FLOW_ORDER_ITEM_STATUSES = [
  'return_requested',
  'return_approved',
  'return_completed',
  'refund_in_progress',
  'refund_complete',
];

export const isOrderItemInReturnFlow = (status: any): boolean =>
  RETURN_FLOW_ORDER_ITEM_STATUSES.includes(String(status ?? '').toLowerCase());

// counts order items per return stage and sums their refund amounts
// - pendingAmount: awaiting vendor/admin decision (return_requested)
// - approvedAmount: approved, not yet refunded (return_approved, return_completed, refund_in_progress)
// - requestedAmount: pendingAmount + approvedAmount (everything not yet refunded)
export const getOrderReturnSummary = (orderItems: any[] = []) => {
  const summary = {
    totalItems: orderItems.length,
    requested: 0,
    approved: 0,
    refundInProgress: 0,
    refunded: 0,
    pendingAmount: 0,
    approvedAmount: 0,
    requestedAmount: 0,
    refundedAmount: 0,
  };

  orderItems.forEach((item: any) => {
    const status = String(item?.status ?? '').toLowerCase();
    if (status === 'return_requested') {
      summary.requested++;
      summary.pendingAmount += getOrderItemRefundValue(item);
    } else if (status === 'return_approved' || status === 'return_completed') {
      summary.approved++;
      summary.approvedAmount += getOrderItemRefundValue(item);
    } else if (status === 'refund_in_progress') {
      summary.refundInProgress++;
      summary.approvedAmount += getOrderItemRefundValue(item);
    } else if (status === 'refund_complete') {
      summary.refunded++;
      summary.refundedAmount += Number(item?.refund_amount) || 0;
    }
  });
  summary.requestedAmount = summary.pendingAmount + summary.approvedAmount;

  return summary;
};

// non-empty return stages of a summary, in lifecycle order, for badge rendering
export const getOrderReturnSummaryBadges = (
  summary: ReturnType<typeof getOrderReturnSummary>,
  t: any
) =>
  [
    { key: 'return-requested', count: summary.requested, label: t('return_requested') },
    { key: 'return-approved', count: summary.approved, label: t('return_approved') },
    { key: 'refund-in-progress', count: summary.refundInProgress, label: t('refund_in_progress') },
    { key: 'refunded', count: summary.refunded, label: t('refunded') },
  ].filter((badge) => badge.count > 0);

export const ORDER_REPORT_SLUG = 'order_report';
export const TRANSACTION_SLUG = 'transaction';
export const SEARCH_TERMS_SLUG = 'search_terms';
export const SMS_LOG_SLUG = 'sms_log';
// sms status
export const SMS_STATUS_SENT_SLUG = 'sent';
export const SMS_STATUS_FAILED_SLUG = 'failed';
// search location
export const SEARCH_LOCATION_GLOBAL_SLUG = 'global_search';
export const SEARCH_LOCATION_PRODUCT_SLUG = 'product_search';
// transaction type
export const TRANSACTION_TYPE_ORDER_SLUG = 'order';
export const TRANSACTION_TYPE_SUBSCRIPTION_SLUG = 'subscription';
// payment gateway
export const PAYMENT_GATEWAY_COD_SLUG = 'cod';
export const PAYMENT_GATEWAY_AUTHORIZE_NET_SLUG = 'authorize_net';
export const PAYMENT_GATEWAY_MX_MERCHANT_SLUG = 'mx-merchant';
export const MOBILE_CODE_OPTION_LIST = [
  { code: DEFAULT_MOBILE_CODE, label: MOBILE_CODE_LABEL_VAL },
];
// export const displayMobileNumberWithCode = (mobile: any, mobileCode = '') => {
//   // const mobileCodeVal = mobileCode ? mobileCode : DEFAULT_MOBILE_CODE;
//   // return `${mobileCodeVal ? `+${String(mobileCodeVal).replace(/\+/g, '')}` : ''}${mobile ? `${mobile}` : ''}`;

//   const code = mobileCode ? mobileCode : DEFAULT_MOBILE_CODE;
//   const cleaned = String(mobile).replace(/\D/g, '');

//   if (cleaned.length === 10) {
//     return `+${String(code).replace(/\+/g, '')} (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
//   }

//   return `+${String(code).replace(/\+/g, '')}${cleaned}`;
// };
export const displayMobileNumberWithCode = (mobile: any, mobileCode = '') => {
  // const mobileCodeVal = mobileCode ? mobileCode : DEFAULT_MOBILE_CODE;
  // return `${mobileCodeVal ? `+${String(mobileCodeVal).replace(/\+/g, '')}` : ''}${mobile ? `${mobile}` : ''}`;

  const code = mobileCode ? mobileCode : DEFAULT_MOBILE_CODE;
  const cleaned = String(mobile).replace(/\D/g, '');

  // grouped the same way the phone inputs format as you type (231-313-9966), so a
  // number reads identically whether it is being displayed or edited
  if (cleaned.length === 10) {
    return `+${String(code).replace(/\+/g, '')} ${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return `+${String(code).replace(/\+/g, '')}${cleaned}`;
};
// US style without the country code - (214) 582-0982; anything that isn't 10 digits is shown as-is
export const displayUSPhoneNumber = (mobile: any) => {
  const cleaned = String(mobile ?? '').replace(/\D/g, '');

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return cleaned;
};
export const displayAddress = (
  add1: string | null,
  pincode: { name?: string } | null,
  _country?: { name?: string } | null,
  state?: { name?: string } | null,
  city?: { name?: string } | null
) => {
  const base = [add1, city?.name, state?.name].filter(Boolean).join(', ');
  const pin = pincode?.name ? `${base ? ' - ' : ''}${pincode.name}` : '';
  return `${base}${pin}`;
};
export const getProductItemAvailability = (product: any) => {
  const options = [
    product?.is_in_store && 'In-Store',
    product?.is_local_pickup && 'Local Pickup',
    product?.is_online && 'Online',
  ];
  const filtered = options.filter(Boolean);
  return filtered.length > 0 ? filtered.join(', ') : '—';
};

// A product supports Instant Checkout only when its FFL has a connected payment gateway.
// Every customer product/cart API resolves that server-side into is_instant_checkout.
export const isProductInstantCheckout = (product: any): boolean =>
  Boolean(product?.is_instant_checkout);

// Single source of truth for a product's handling time on every customer surface (card,
// detail, cart). Mirrors the API's shipping-rate calculation: the product's own handling
// time wins, falling back to the FFL's default (ffl_handling_time on detail/cart, or the
// legacy ffl.min/max_handling_time on listing).
export const getProductHandlingTime = (product: any) => {
  const toDays = (value: any) => {
    const days = Number(value);
    return Number.isFinite(days) && days > 0 ? days : null;
  };
  const sources = [product, product?.ffl_handling_time, product?.ffl];
  for (const source of sources) {
    if (!source?.max_handling_time) continue;
    const max = toDays(source?.max_handling_time);
    if (max) {
      return { min: toDays(source?.min_handling_time) ?? max, max };
    }
  }
  return null;
};

// The seller offers free shipping on this product. Its shipping cost fields can still hold an old
// amount from before it was switched, so cards must check this first instead of trusting them.
export const isProductFreeShipping = (product: any): boolean =>
  product?.shipping_type === 'free_shipping';

// Single source of truth for the shipping amount shown on product cards (shop card,
// compare cards, Compare "Add Item" popup): the product's own shipping cost, else the
// lowest calculated rate, else 0. Cards show a shipping line only when this is > 0.
export const getProductShippingCost = (product: any): number =>
  isProductFreeShipping(product)
    ? 0
    : Number(product?.shipping_cost || product?.min_shipping_cost || 0);

// One rule for "free delivery", from an item of the checkout calculation: a free-shipping rule fired,
// or a plain delivery costs nothing. Local pickup and manual-shipping items (priced later) never
// count, and neither does an item the quote has not covered yet.
export const isFreeDeliveryCalculationItem = (calcItem: any): boolean =>
  !!calcItem &&
  (calcItem.free_shipping_rule_triggered === true ||
    (Number(calcItem.shipping_cost) === 0 &&
      calcItem.delivery_type === 'delivery' &&
      !calcItem.is_manual_shipping));

export const isOvernightDeliveryEligible = (product: any) => {
  const handlingTime = getProductHandlingTime(product);
  return !!handlingTime && handlingTime.min <= 1;
};

export const getSocialUrl = (
  platform: 'instagram' | 'facebook' | 'youtube' | 'twitter',
  slug?: string | null
) => {
  if (!slug) return undefined;

  const urls = {
    instagram: `https://www.instagram.com/${slug}`,
    facebook: `https://www.facebook.com/${slug}`,
    youtube: `https://www.youtube.com/@${slug}`,
    twitter: `https://twitter.com/${slug}`,
  };

  return urls[platform];
};

export const formatQuantity = (qty: number | string | undefined | null) => {
  if (qty === null || qty === undefined || qty === '') return '-';
  const num = Number(qty);
  if (isNaN(num)) return qty;
  if (num >= 1e12) return (num / 1e12).toFixed(1).replace(/\.0$/, '') + 'T';
  if (num >= 1e9) return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

// target type
export const getTargetTypeList = (t: any) => [
  { slug: 'product', name: t('product') },
  { slug: 'category', name: t('category') },
  { slug: 'cms', name: t('cms') },
];
export const getTargetTypeBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getTargetTypeList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// status
export const PENDING_STATUS_SLUG = 'pending';
export const PAYMENT_FAILED_STATUS_SLUG = 'payment_failed';
export const PAID_STATUS_SLUG = 'paid';
export const FAILED_STATUS_SLUG = 'failed';
export const IN_PROGRESS_STATUS_SLUG = 'in_progress';
export const SUBSCRIBED_STATUS_SLUG = 'subscribed';
export const APPROVED_STATUS_SLUG = 'approved';
export const REJECTED_STATUS_SLUG = 'rejected';
export const SHIPPED_STATUS_SLUG = 'shipped';
export const READY_TO_SHIPMENT_STATUS_SLUG = 'ready_to_shipment';
export const READY_TO_PICKUP_STATUS_SLUG = 'ready_to_pickup';
export const CANCELLED_STATUS_SLUG = 'cancelled';
export const COMPLETED_STATUS_SLUG = 'completed';
export const ACTIVE_STATUS_SLUG = 'active';
// featured
export const getFeaturedList = (t: any) => [
  { id: true, name: t('yes') },
  { id: false, name: t('no') },
];
export const getFeaturedById = (id: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getFeaturedList(t).filter(function (item) {
    return item.id == id;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// status
export const getStatusList = (t: any) => [
  { id: true, name: t('active') },
  { id: false, name: t('inactive') },
];
export const getYesNoList = (t: any) => [
  { id: true, name: t('yes') },
  { id: false, name: t('no') },
];
// listing status - derived from whether anything is actually listed, not from a stored flag
export const LISTED_STATUS_SLUG = 'listed';
export const NOT_LISTED_STATUS_SLUG = 'not_listed';
export const getListingStatusList = (t: any) => [
  { slug: LISTED_STATUS_SLUG, name: t('active') },
  { slug: NOT_LISTED_STATUS_SLUG, name: t('inactive') },
];
export const getStatusById = (id: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getStatusList(t).filter(function (item) {
    return item.id == id;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// order status
export const getOrderStatusList = (t: any) => [
  { slug: PENDING_STATUS_SLUG, name: t('pending') },
  { slug: PAYMENT_FAILED_STATUS_SLUG, name: t('payment_failed') },
  { slug: PAID_STATUS_SLUG, name: t('paid') },
  { slug: 'transfer_request', name: t('transfer_request') },
  { slug: READY_TO_SHIPMENT_STATUS_SLUG, name: t('ready_to_shipment') },
  { slug: READY_TO_PICKUP_STATUS_SLUG, name: t('ready_for_pickup') },
  { slug: SHIPPED_STATUS_SLUG, name: t('shipped') },
  { slug: COMPLETED_STATUS_SLUG, name: t('completed') },
  {
    slug: 'cancelled',
    name: t('cancelled'),
  },
];
export const getOrderStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getOrderStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// order item status
export const getOrderItemStatusList = (t: any) => [
  { slug: PENDING_STATUS_SLUG, name: t('pending') },
  { slug: PAYMENT_FAILED_STATUS_SLUG, name: t('payment_failed') },
  { slug: PAID_STATUS_SLUG, name: t('paid') },
  { slug: 'transfer_request', name: t('transfer_request') },
  { slug: SHIPPED_STATUS_SLUG, name: t('shipped') },
  { slug: COMPLETED_STATUS_SLUG, name: t('completed') },
  {
    slug: 'cancelled',
    name: t('cancelled'),
  },
];
export const getOrderItemStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getOrderItemStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// order cancellation reasons. The backend stores the reason as free text, so the label
// is what gets sent - keep these stable so cancelled orders stay comparable.
export const CANCEL_REASON_OTHER_SLUG = 'other';
export const getOrderCancellationReasonList = (t: any) => [
  { slug: 'buyer_asked_to_cancel', name: t('reason_buyer_asked_to_cancel') },
  { slug: 'out_of_stock', name: t('reason_out_of_stock') },
  { slug: 'problem_with_buyer_address', name: t('reason_problem_with_buyer_address') },
  { slug: 'duplicate_order', name: t('reason_duplicate_order') },
  { slug: CANCEL_REASON_OTHER_SLUG, name: t('reason_other') },
];

export const getProductHelpReasonList = (t: any) => [
  {
    slug: 'suggestion',
    name: t('reason_suggestion', { defaultValue: 'Suggestion' }),
  },
  {
    slug: 'not_up_to_mark',
    name: t('reason_not_up_to_mark', { defaultValue: 'Not up to the mark' }),
  },
  {
    slug: 'product_creation_flow',
    name: t('reason_product_creation_flow', {
      defaultValue: 'Suggestion for the product creation flow',
    }),
  },
  { slug: REPORT_REASON_OTHER_SLUG, name: t('reason_other') },
];

export const REPORT_REASON_OTHER_SLUG = 'other';
export const getOrderReportReasonList = (t: any) => [
  {
    slug: 'non_responsive',
    name: t('reason_non_responsive', { defaultValue: 'Non-responsive buyer' }),
  },
  { slug: 'payment_issue', name: t('reason_payment_issue', { defaultValue: 'Refusing to pay' }) },
  { slug: 'fraud', name: t('reason_fraud', { defaultValue: 'Fraudulent activity' }) },
  { slug: 'abusive', name: t('reason_abusive', { defaultValue: 'Abusive language' }) },
  { slug: REPORT_REASON_OTHER_SLUG, name: t('reason_other') },
];

export const INTEGRATION_REQUEST_OTHER_SLUG = 'other';
export const getIntegrationRequestReasonList = (t: any) => [
  {
    slug: 'payment_gateway',
    name: t('reason_payment_gateway', { defaultValue: 'Payment Gateway' }),
  },
  {
    slug: 'shipping_carrier',
    name: t('reason_shipping_carrier', { defaultValue: 'Shipping Carrier / Logistics' }),
  },
  {
    slug: 'pos_system',
    name: t('reason_pos_system', { defaultValue: 'POS System' }),
  },
  {
    slug: 'inventory_management',
    name: t('reason_inventory_management', { defaultValue: 'Inventory / ERP Management' }),
  },
  { slug: INTEGRATION_REQUEST_OTHER_SLUG, name: t('reason_other', { defaultValue: 'Other' }) },
];
export const PAYMENT_STATUS_SUCCESS_SLUG = 'success';
export const PAYMENT_STATUS_PENDING_SLUG = 'pending';
export const PAYMENT_STATUS_FAILED_SLUG = 'failed';
// Payment status
export const getPaymentStatusList = (t: any) => [
  { slug: PAYMENT_STATUS_PENDING_SLUG, name: t('pending') },
  { slug: PAYMENT_STATUS_SUCCESS_SLUG, name: t('success') },
  {
    slug: PAYMENT_STATUS_FAILED_SLUG,
    name: t('failed'),
  },
];
export const getPaymentStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getPaymentStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// Transaction type
export const getTransactionTypeList = (t: any) => [
  { slug: TRANSACTION_TYPE_ORDER_SLUG, name: t('order') },
  { slug: TRANSACTION_TYPE_SUBSCRIPTION_SLUG, name: t('subscription') },
];
export const getTransactionTypeBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getTransactionTypeList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// product filter
export const PRODUCT_FILTER_NFA_ITEM = 'nfa_item';
// product sort options
export const PRODUCT_SORT_BY_LATEST = 'latest';
export const PRODUCT_SORT_BY_TOP_DEAL = 'top_deal';
export const PRODUCT_SORT_BY_NEW_LISTED = 'newly_listed';
export const PRODUCT_SORT_BY_NEAREST_DISTANCE = 'distance_nearest';
export const PRODUCT_SORT_BY_PRICE_LOW_HIGH = 'price_low_high';
export const PRODUCT_SORT_BY_PRICE_HIGH_LOW = 'price_high_low';
export const PRODUCT_SORT_BY_BEST_MATCH = 'best_match';
export const PRODUCT_SORT_BY_TOP_SELLER = 'top_seller';
export const getProductSortList = (t: any) => [
  { label: t('Latest'), value: PRODUCT_SORT_BY_LATEST },
  { label: 'New Listed', value: PRODUCT_SORT_BY_NEW_LISTED },
  { label: 'Price: Low to High', value: PRODUCT_SORT_BY_PRICE_LOW_HIGH },
  { label: 'Price: High to Low', value: PRODUCT_SORT_BY_PRICE_HIGH_LOW },
  { label: 'Nearest Distance', value: PRODUCT_SORT_BY_NEAREST_DISTANCE },
  { label: 'Best Match', value: PRODUCT_SORT_BY_BEST_MATCH },
  { label: t('top_deals'), value: PRODUCT_SORT_BY_TOP_DEAL },
  { label: t('top_seller'), value: PRODUCT_SORT_BY_TOP_SELLER },
];

export const getMercandiesProductSortList = (t: any) => [
  { label: t('Sort By Latest'), value: PRODUCT_SORT_BY_LATEST },
  { label: 'New Listed', value: PRODUCT_SORT_BY_NEW_LISTED },
  { label: 'Price: Low to High', value: PRODUCT_SORT_BY_PRICE_LOW_HIGH },
  { label: 'Price: High to Low', value: PRODUCT_SORT_BY_PRICE_HIGH_LOW },
];
// Search Location
export const getSearchLocationList = (t: any) => [
  { slug: SEARCH_LOCATION_GLOBAL_SLUG, name: t('global_search') },
  { slug: SEARCH_LOCATION_PRODUCT_SLUG, name: t('product_search') },
];
export const getSearchLocationBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getSearchLocationList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// SMS Status
export const getSmsStatusList = (t: any) => [
  { slug: SMS_STATUS_SENT_SLUG, name: t('sent') },
  { slug: SMS_STATUS_FAILED_SLUG, name: t('failed') },
];
export const getSmsStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getSmsStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// Payment gateway
export const getPaymentGatewayList = (t: any) => [
  { slug: PAYMENT_GATEWAY_AUTHORIZE_NET_SLUG, name: t('authorize_net') },
  { slug: PAYMENT_GATEWAY_MX_MERCHANT_SLUG, name: t('mx_merchant') },
];
export const getPaymentGatewayBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getPaymentGatewayList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// Payment type: how the customer paid. Slugs match the API's PaymentTypeEnum.
export const PAYMENT_TYPE_CARD_SLUG = 'card';
export const PAYMENT_TYPE_GOOGLE_PAY_SLUG = 'Gpay';
export const PAYMENT_TYPE_APPLE_PAY_SLUG = 'ApplePay';
export const PAYMENT_TYPE_PURCHASE_FORM_SLUG = 'purchase_form';
export const getPaymentTypeList = (t: any) => [
  { slug: PAYMENT_TYPE_CARD_SLUG, name: t('payment_type_card') },
  { slug: PAYMENT_TYPE_GOOGLE_PAY_SLUG, name: t('google_pay') },
  { slug: PAYMENT_TYPE_APPLE_PAY_SLUG, name: t('apple_pay') },
  { slug: PAYMENT_TYPE_PURCHASE_FORM_SLUG, name: t('purchase_form') },
];
export const getPaymentTypeBySlug = (slug: any, t: any) =>
  getPaymentTypeList(t).find((item) => item.slug == slug) || null;
// A transaction's payment type. Rows saved before transactions stored it have none - for
// those, a card number means it was paid by card.
export const getTransactionPaymentType = (transaction: any): string | null =>
  transaction?.payment_type || (transaction?.card_last_four ? PAYMENT_TYPE_CARD_SLUG : null);
// Message To options
export const MESSAGE_TO_SELECTED_FFL_SLUG = 'selected_ffl';
export const MESSAGE_TO_SELECTED_CUSTOMER_SLUG = 'selected_customer';
export const MESSAGE_TO_SELECTED_CUSTOMER_GROUP_SLUG = 'selected_customer_group';
// `name` is the dropdown option (filter, send form); `badgeName` overrides it on the list's
// "To:" badge, where both customer audiences read "Customers" (Figma)
export const getMessageToList = (t: any) => [
  { slug: 'approved_ffl', name: t('approved_ffl'), class: 'message-to-approved' },
  { slug: 'rejected_ffl', name: t('rejected_ffl'), class: 'message-to-rejected' },
  { slug: MESSAGE_TO_SELECTED_FFL_SLUG, name: t('selected_ffl'), class: 'message-to-selected' },
  {
    slug: MESSAGE_TO_SELECTED_CUSTOMER_SLUG,
    name: t('selected_customer'),
    badgeName: t('customers'),
    class: 'message-to-customer',
  },
  {
    slug: MESSAGE_TO_SELECTED_CUSTOMER_GROUP_SLUG,
    name: t('selected_customer_group'),
    badgeName: t('customers'),
    class: 'message-to-customer',
  },
];
export const getMessageToBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getMessageToList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};

export const getMessageFFLTabs = (t: any) => [
  { value: 'all', label: t('all') },
  { value: APPROVED_STATUS_SLUG, label: t('approved_ffl') },
  { value: PENDING_STATUS_SLUG, label: t('pending_ffl') },
  { value: REJECTED_STATUS_SLUG, label: t('rejected_ffl') },
];

// Account Status
export const getAccountStatusList = (t: any) => [
  { slug: PENDING_STATUS_SLUG, name: t('pending'), badgeBg: 'warning' },
  { slug: APPROVED_STATUS_SLUG, name: t('approved'), badgeBg: 'success' },
  { slug: REJECTED_STATUS_SLUG, name: t('rejected'), badgeBg: 'danger' },
];
export const getAccountStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getAccountStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// FFL Status
export const getFFLStatusList = (t: any) => [
  { slug: PENDING_STATUS_SLUG, name: t('pending'), badgeBg: 'warning' },
  { slug: APPROVED_STATUS_SLUG, name: t('approved'), badgeBg: 'success' },
  { slug: REJECTED_STATUS_SLUG, name: t('rejected'), badgeBg: 'danger' },
];
export const getFFLStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getFFLStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// Rating List
export const getRatingList = (t: any) => [
  { value: 5, name: t('no_star', { key: 5 }) },
  { value: 4, name: t('no_star', { key: 4 }) },
  { value: 3, name: t('no_star', { key: 3 }) },
  { value: 2, name: t('no_star', { key: 2 }) },
  { value: 1, name: t('no_star', { key: 1 }) },
];
// state obj
export const getStateObj = (element: any, setDisabled = false) => {
  let obj = {
    label: element.full_name || element.name,
    name: element.name,
    value: element.id,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// ffl Allow Ship
export const getAllowToShipList = (t: any) => [
  { slug: true, name: t('yes') },
  { slug: false, name: t('no') },
];
export const getAllowShipBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getAllowToShipList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};

// get Payment Type obj
export const getPaymentTypeObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id || element._id,
    name: `${element.name}`,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// get category obj
export const getCategoryObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id,
    name: `${element.name}`,
    is_firearm_category: element.is_firearm_category,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// get ffl obj
export const getFFLObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id,
    slug: element.slug,
    business_name: `${element.business_name}`,
    name: element.user ? getFullName(element.user.first_name, element.user.last_name) : '',
    email: element?.user?.email,
    status: element?.ffl_verified,
    avatar: element?.user?.profile_pic,
    allow_merchandise: element.allow_merchandise || false,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// selected FFL name is truncated to this length in the header banners, full name stays in the title
export const SHIPPING_FFL_NAME_MAX_LENGTH = 12;
// get shipping ffl obj
export const getShippingFFLObj = (element: any) => {
  const obj = {
    ...element,
    label: element?.ffl?.business_name ?? element?.license_name ?? element?.business_name,
  };
  return obj;
};
// Same "isApproved" distinction the FFL listing cards use for their badge - a claimed/registered FFL
// has its business profile nested under .ffl, while a bare unclaimed ATF license record does not.
export const isRegisteredFfl = (selectedFfl: any) => !!selectedFfl?.ffl;
export const getShippingFFLObjForDB = (element: any) => {
  const obj = {
    ...element,
    label: element?.ffl?.business_name ?? element?.license_name,
    license_name: element?.license_name ?? element.business_name ?? '',
    business_name: element?.ffl?.business_name ?? element?.business_name ?? element?.license_name,
  };
  return obj;
};
export const getNormalizedShippingFFLPayload = (fflObj: any) => {
  if (!fflObj || typeof fflObj !== 'object' || Array.isArray(fflObj)) {
    return null;
  }

  const rawNo = String(fflObj.ffl_no || '').replace(/[^0-9A-Z]/gi, '');
  let lic_regn = fflObj.lic_regn ?? fflObj.ffl_detail?.lic_regn ?? '';
  let lic_dist = fflObj.lic_dist ?? fflObj.ffl_detail?.lic_dist ?? '';
  let lic_cnty = fflObj.lic_cnty ?? fflObj.ffl_detail?.lic_cnty ?? '';
  let lic_type = fflObj.lic_type ?? fflObj.ffl_detail?.lic_type ?? '';
  let lic_xprdte = fflObj.lic_xprdte ?? fflObj.ffl_detail?.lic_xprdte ?? '';
  let lic_seqn = fflObj.lic_seqn ?? fflObj.ffl_detail?.lic_seqn ?? '';

  if (!lic_regn && rawNo.length >= 15) {
    lic_regn = rawNo.substring(0, 1);
    lic_dist = rawNo.substring(1, 3);
    lic_cnty = rawNo.substring(3, 6);
    lic_type = rawNo.substring(6, 8);
    lic_xprdte = rawNo.substring(8, 10);
    lic_seqn = rawNo.substring(10);
  }

  return {
    lic_regn: String(lic_regn || '').trim(),
    lic_dist: String(lic_dist || '').trim(),
    lic_cnty: String(lic_cnty || '').trim(),
    lic_type: String(lic_type || '').trim(),
    lic_xprdte: String(lic_xprdte || '').trim(),
    lic_seqn: String(lic_seqn || '').trim(),
    license_name:
      fflObj?.license_name ?? fflObj?.business_name ?? fflObj?.ffl_detail?.license_name ?? '',
    business_name:
      fflObj?.business_name ?? fflObj?.license_name ?? fflObj?.ffl_detail?.business_name ?? '',
    premise_street:
      fflObj?.premise_street ?? fflObj?.add1 ?? fflObj?.ffl_detail?.premise_street ?? '',
    premise_city:
      fflObj?.premise_city ??
      fflObj?.city?.name ??
      fflObj?.city_name ??
      fflObj?.ffl_detail?.premise_city ??
      '',
    premise_state:
      fflObj?.premise_state ??
      fflObj?.state?.name ??
      fflObj?.state_name ??
      fflObj?.ffl_detail?.premise_state ??
      '',
    premise_zip_code:
      fflObj?.premise_zip_code ??
      fflObj?.pincode?.name ??
      fflObj?.zipcode ??
      fflObj?.ffl_detail?.premise_zip_code ??
      '',
    mail_street: fflObj?.mail_street ?? fflObj?.ffl_detail?.mail_street ?? '',
    mail_city: fflObj?.mail_city ?? fflObj?.ffl_detail?.mail_city ?? '',
    mail_state: fflObj?.mail_state ?? fflObj?.ffl_detail?.mail_state ?? '',
    mail_zip_code: fflObj?.mail_zip_code ?? fflObj?.ffl_detail?.mail_zip_code ?? '',
    voice_phone:
      fflObj?.voice_phone ??
      fflObj?.mobile ??
      fflObj?.contact_number ??
      fflObj?.ffl_detail?.voice_phone ??
      '',
  };
};

const hasFflLicenseParts = (fflObj: any) =>
  Boolean(
    fflObj &&
    typeof fflObj === 'object' &&
    (fflObj.lic_regn ||
      fflObj.ffl_detail?.lic_regn ||
      String(fflObj.ffl_no || '').replace(/[^0-9A-Z]/gi, '').length >= 15)
  );

// The receiving-FFL payload for checkout calculation / place order. A picked FFL is the ATF
// license record (registered ones carry their business profile under .ffl) - send the record
// itself when it has the license number, the profile only as a fallback.
export const getShippingFFLPayloadFromSelection = (selectedFfl: any) => {
  if (!selectedFfl || typeof selectedFfl !== 'object') return null;
  const source = hasFflLicenseParts(selectedFfl)
    ? selectedFfl
    : hasFflLicenseParts(selectedFfl.ffl)
      ? selectedFfl.ffl
      : selectedFfl;
  return getNormalizedShippingFFLPayload(source);
};

// Same FFL? Compared by license number, since the same FFL can come with or without an id.
export const getShippingFFLKey = (selectedFfl: any) => {
  const payload = getShippingFFLPayloadFromSelection(selectedFfl);
  if (!payload) return '';
  return [
    payload.lic_regn,
    payload.lic_dist,
    payload.lic_cnty,
    payload.lic_type,
    payload.lic_xprdte,
    payload.lic_seqn,
  ].join('-');
};
// get customer obj
export const getCustomerObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id,
    name: getFullName(element.first_name, element.last_name),
    email: element?.email,
    avatar: element?.profile_pic,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// get customer group obj
export const getCustomerGroupObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id,
    name: element.name,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// get role obj
export const getRoleObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id,
    name: element.name,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// shipping LIST
export const FREE_SHIPPING_TYPE_SLUG = 'free_shipping';
export const ACTUAL_SHIPPING_TYPE_SLUG = 'actual';
export const FLAT_RATE_SHIPPING_TYPE_SLUG = 'flat_rate';
export const BUYER_PAYS_ACTUAL_SHIPPING_COST_SLUG = 'actual';
export const BUYER_PAYS_FLAT_SHIPPING_RATE_SLUG = 'buyer_pays_flat_shipping_rate';
export const getShippingList = (t: any) => [
  { slug: FREE_SHIPPING_TYPE_SLUG, name: t('free_shiping') },
  { slug: BUYER_PAYS_ACTUAL_SHIPPING_COST_SLUG, name: t('buyer_pays_actual_shipping_cost') },
  { slug: BUYER_PAYS_FLAT_SHIPPING_RATE_SLUG, name: t('buyer_pays_fixed_amount') },
];
export const getShippingBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getShippingList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// get Shipping Configuration obj
export const getShippingConfigurationObj = (element: any, setDisabled = false) => {
  if (!element) return null;
  let obj = {
    ...element,
    id: element.id || element._id,
    name: element.name || `${element.carrier_name || ''} - ${element.service_name || ''}`,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
// product type
export const PRODUCT_TYPE_DIRECT_SLUG = 'direct_shipping';
export const PRODUCT_TYPE_FFL_SLUG = 'ffl_shipping';
// A seller's checkout group can mix product types. One FFL-shipping item makes the whole group an
// FFL transfer (mirrors validateCartItemsGrouping in the API), whatever order the items are in.
export const getGroupProductType = (items: any[] = []): string => {
  const slugs = items.map((item) => {
    const type = item?.product?.product_type;
    return String(type?.slug ?? type ?? '');
  });
  return slugs.includes(PRODUCT_TYPE_FFL_SLUG) ? PRODUCT_TYPE_FFL_SLUG : slugs[0] || '';
};
export const getProductTypeList = (t: any) => [
  { slug: PRODUCT_TYPE_DIRECT_SLUG, name: t('direct_shipping') },
  { slug: PRODUCT_TYPE_FFL_SLUG, name: t('ffl_shipping') },
];
export const getProductTypeBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getProductTypeList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};

//...
export const CATEGORY_LIST = [
  { id: 'Rifles', name: 'Rifles' },
  { id: 'Shortguns', name: 'Shortguns' },
  { id: 'Handguns', name: 'Handguns' },
  { id: 'NFA / Class III', name: 'NFA / Class III' },
  { id: 'Ammunition', name: 'Ammunition' },
  { id: 'Lower Receivers', name: 'Lower Receivers' },
  { id: 'Accessories & Optics', name: 'Accessories & Optics' },
];
export const SUB_CATEGORY_LIST = [
  { id: 'Bolt Action', name: 'Bolt Action' },
  { id: 'Semi-Auto', name: 'Semi-Auto' },
  { id: 'Single Shot', name: 'Single Shot' },
  { id: 'Lever Action', name: 'Lever Action' },
  { id: 'CA Compliant', name: 'CA Compliant' },
];
export const ACTION_LIST = [
  { id: 'Bolt Action', name: 'Bolt Action' },
  { id: 'Semi-Auto', name: 'Semi-Auto' },
  { id: 'Full Auto', name: 'Full Auto' },
  { id: 'Lever Action', name: 'Lever Action' },
  { id: 'Pump Action', name: 'Pump Action' },
  { id: 'Break Action', name: 'Break Action' },
];
export const CONDITION_LIST = [
  { id: 'Brand New', name: 'Brand New' },
  { id: 'Used', name: 'Used' },
];
export const CALIBER_LIST = [
  { id: '5.56', name: '5.56' },
  { id: '12 gauge', name: '12 gauge' },
  { id: '9mm', name: '9mm' },
  { id: '5.56/.223', name: '5.56/.223' },
  { id: '.45', name: '.45' },
  { id: '357 Magnum', name: '357 Magnum' },
  { id: '.308 WIN/7.62x51', name: '.308 WIN/7.62x51' },
  { id: '7.62', name: '7.62' },
  { id: '9', name: '9' },
  { id: '6.5 Creedmor', name: '6.5 Creedmor' },
];

export const ACQUISTION_TYPE_LIST = [
  { id: 'consignment', name: 'Consignment' },
  { id: 'dealer_transfer', name: 'Dealer Transfer' },
  { id: 'gunsmithing', name: 'Gunsmithing' },
  { id: 'nfa', name: 'NFA' },
  { id: 'pawn', name: 'Pawn' },
  { id: 'private_party_transfer', name: 'Private Party Transfer' },
  { id: 'purchase', name: 'Purchase' },
  { id: 'return', name: 'Return' },
  { id: 'trade', name: 'Trade' },
  { id: 'any_other', name: 'Any Other' },
];

export const ITEM_TYPE_LIST = [
  { id: 'combination', name: 'Combination' },
  { id: 'frame', name: 'Frame' },
  { id: 'nfa_any_other_weapon', name: 'NFA/Any Other Weapon' },
  { id: 'nfa_destructive_device', name: 'NFA/Destructive Device' },
  { id: 'nfa_machine_gun', name: 'NFA/Machine Gun' },
  { id: 'nfa_short_barrel_rifle', name: 'NFA/Short Barrel Rifle' },
  { id: 'nfa_short_barrel_shotgun', name: 'NFA/Short Barrel Shotgun' },
  { id: 'nfa_silencer', name: 'NFA/Silencer' },
  { id: 'other', name: 'Other' },
  { id: 'pistol', name: 'Pistol' },
  { id: 'pistol_grip_firearm', name: 'Pistol Grip Firearm' },
  { id: 'receiver', name: 'Receiver' },
  { id: 'revolver', name: 'Revolver' },
  { id: 'rifle', name: 'Rifle' },
  { id: 'shotgun', name: 'Shotgun' },
  { id: 'any_other', name: 'Any Other' },
];

export const FFL_NAME_LIST = [{ id: 'My Local Gun Shop', name: 'My Local Gun Shop' }];

export const DRAFT_STATUS_SLUG = 'draft';
export const PUBLISH_STATUS_SLUG = 'published';
export const ARCHIVED_STATUS_SLUG = 'archived';
export const getBlogStatusList = (t: any) => [
  { slug: DRAFT_STATUS_SLUG, name: t('draft'), badgeBg: 'black' },
  { slug: PUBLISH_STATUS_SLUG, name: t('blog_publish'), badgeBg: 'success' },
  { slug: ARCHIVED_STATUS_SLUG, name: t('archived'), badgeBg: 'danger' },
];

// admin blog list / grid: "<author> - <admin who created it>" on one line (either part may be missing)
export const getBlogAuthorLine = (row: any) => {
  const createdBy = row?.created_by
    ? getFullName(row.created_by.first_name, row.created_by.last_name)
    : '';
  return [row?.author, createdBy].filter(Boolean).join(' - ') || '-';
};

export const getBlogStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getBlogStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
export const BLOG_TRENDING_CATEGORY_SLUG = 'trending';
export const ALL_SLUG = 'all';
export const getFieldTypesOptions = (t: any) => [
  { value: t('text_value'), label: t('text_label') },
  { value: t('number_value'), label: t('number_label') },
  { value: t('boolean_value'), label: t('boolean_label') },
  { value: t('select_value'), label: t('select') },
  { value: t('multi_select_value'), label: t('multi_select_label') },
];

export const AD_PUBLISH_STATUS_SLUG = 'publish';
export const AD_UNPUBLISH_STATUS_SLUG = 'unpublish';
export const getAdvertisementStatusList = (t: any) => [
  { slug: AD_PUBLISH_STATUS_SLUG, name: t('publish'), badgeBg: 'success' },
  { slug: AD_UNPUBLISH_STATUS_SLUG, name: t('unpublish'), badgeBg: 'danger' },
];

export const getAdvertisementStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getAdvertisementStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};

export const editorFlags = {
  toolbar: true,
  blockType: true,
  fontSize: true,
  fontFamily: true,
  undoRedo: true,
  bold: true,
  italic: true,
  underline: true,
  inlineCode: true,
  link: true,
  image: true,
  textColor: true,
  highlight: true,
  textCase: true,
  insertMenu: true,
  list: true,
  quote: true,
  codeBlock: true,
  alignment: true,
};
export const getApplicableTypeList = (t: any) => [
  { slug: 'all', name: t('all') },
  { slug: 'firearm', name: t('firearm') },
  { slug: 'merchandise', name: t('merchandise') },
];

export const getApplicableTypeBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getApplicableTypeList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
export const getDiscountTypeList = (t: any) => [
  { slug: 'flast', name: t('flat') },
  { slug: 'percentage', name: t('percentage') },
];
export const getDiscountTypeBySlug = (slug: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getDiscountTypeList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};

export const getProductObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id,
    name: `${element.name}`,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};
export const stripHtmlTags = (value: any) => {
  if (!value) return '';
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const getTextExcerpt = (value: any, maxLength = 180) => {
  const cleanValue = stripHtmlTags(value);
  if (!cleanValue) return '';
  if (cleanValue.length <= maxLength) return cleanValue;
  return `${cleanValue.slice(0, maxLength).trim()}...`;
};

export const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const timeOptions = [
  '6:00 AM',
  '7:00 AM',
  '8:00 AM',
  '9:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '1:00 PM',
  '2:00 PM',
  '3:00 PM',
  '4:00 PM',
  '5:00 PM',
  '6:00 PM',
  '7:00 PM',
  '8:00 PM',
];

// In backed status are in capatilized
export const TR_PENDING_STATUS_SLUG = 'pending';
export const TR_APPROVED_STATUS_SLUG = 'approved';
export const TR_REJECTED_STATUS_SLUG = 'rejected';
// An order item's latest transfer request when its FFL declined it - the item is then waiting for
// another FFL to be picked. null once a newer request exists (or there's no rejected one).
export const getLatestRejectedTransferRequest = (orderItem: any) => {
  const latest = [...(orderItem?.transfer_requests || [])].sort(
    (a: any, b: any) =>
      new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime()
  )[0];
  return latest?.status === TR_REJECTED_STATUS_SLUG ? latest : null;
};
// The FFL a transfer request went to: the registered FFL, or the licence it was invited by
export const getTransferRequestFFLName = (transferRequest: any): string =>
  transferRequest?.destination_ffl?.business_name ||
  transferRequest?.invited_ffl?.business_name ||
  transferRequest?.invited_ffl?.license_name ||
  '';
// export const TR_SHIPPED_STATUS_SLUG = 'shipped';
// export const TR_COMPLETED_STATUS_SLUG = 'completed';

// Transfer request
export const getTransferStatusList = (t: any) => [
  { slug: TR_PENDING_STATUS_SLUG, name: t('pending'), badgeBg: 'warning' },
  { slug: TR_APPROVED_STATUS_SLUG, name: t('approved'), badgeBg: 'success' },
  { slug: TR_REJECTED_STATUS_SLUG, name: t('rejected'), badgeBg: 'danger' },
  // { slug: TR_SHIPPED_STATUS_SLUG, name: t('shipped'), badgeBg: 'success' },
  // { slug: TR_COMPLETED_STATUS_SLUG, name: t('completed'), badgeBg: 'success' },
];
export const getTransferStatusBySlug = (slug: any, t: any) => {
  let selectedSlug = slug;

  const filteredData = getTransferStatusList(t).filter(function (item) {
    return item.slug == slug;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// transfer request direction - pairs with the "FFL" filter to say which side of the
// transfer (source vs destination) the picked FFL should match.
export const TR_DIRECTION_SENDING_SLUG = 'sending';
export const TR_DIRECTION_RECEIVING_SLUG = 'receiving';
export const getTransferDirectionList = (t: any) => [
  { slug: TR_DIRECTION_SENDING_SLUG, name: t('sending') },
  { slug: TR_DIRECTION_RECEIVING_SLUG, name: t('receiving') },
];
// dealer type
export const getDealerTypeOptions = (t: any) => [
  { value: 'type_01', label: t('type_01') },
  { value: 'type_02', label: t('type_02') },
  { value: 'type_03', label: t('type_03') },
  { value: 'type_06', label: t('type_06') },
  { value: 'type_07', label: t('type_07') },
  { value: 'type_08', label: t('type_08') },
  { value: 'type_09', label: t('type_09') },
  { value: 'type_10', label: t('type_10') },
  { value: 'type_11', label: t('type_11') },
];

// get blog author obj
export const getAuthorObj = (element: any) => {
  const obj = {
    id: element.id,
    name: `${element.first_name} ${element.last_name}`,
  };
  return obj;
};

// get manufacturer obj
export const getManufacturerObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element?.id || element?._id || '',
    name: `${element?.name || ''}`,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element?.status ? false : true } };
  }
  return obj;
};
// Blog
export const getBlogImage = (blog: any, size = IMAGE_SIZE.md) =>
  blog?.featured_image || blog?.banner_image
    ? getImageURL(blog?.featured_image || blog?.banner_image, size)
    : DefaultBlogImage;

// Builds the dedicated category/author blog listing URLs from the route
// constants. With no slug (or the "all" slug) it returns the base path
// (e.g. /blogs/author); with a slug it returns /blogs/author/<slug>.
export const getBlogCategoryListURL = (slug?: string) =>
  slug && slug !== ALL_SLUG
    ? placeParamsToURL(customerMainRoutes.blogCategoryList.path, { slug })
    : placeParamsToURL(customerMainRoutes.blogCategoryList.path, { slug: '' }).replace(/\/+$/, '');

export const getBlogAuthorListURL = (slug?: string) =>
  slug && slug !== ALL_SLUG
    ? placeParamsToURL(customerMainRoutes.blogAuthorList.path, { slug })
    : placeParamsToURL(customerMainRoutes.blogAuthorList.path, { slug: '' }).replace(/\/+$/, '');

// tax type
export const INCLUSIVE_TAX_TYPE_SLUG = 'inclusive';
export const EXCLUSIVE_TAX_TYPE_SLUG = 'exclusive';
export const getTaxTypeOptions = (t: any) => [
  { value: EXCLUSIVE_TAX_TYPE_SLUG, label: t('exclusive') },
  { value: INCLUSIVE_TAX_TYPE_SLUG, label: t('inclusive') },
];

// condition type
export const NEW_CONDITION_SLUG = 'new';
export const USED_CONDITION_SLUG = 'used';
export const getConditionOptions = (t: any) => [
  { value: NEW_CONDITION_SLUG, label: t('new') },
  { value: USED_CONDITION_SLUG, label: t('used') },
];
// catalog type

export const FIREARMS_AND_ACCESSORIES_SLUG = ProductCatalogType.FirearmsAndAccessories;
export const AMMUNITION_CATALOG_SLUG = ProductCatalogType.Ammunition;
export const SILENCERS_CATALOG_SLUG = ProductCatalogType.Silencers;
export const MERCHANDISE_SLUG = ProductCatalogType.Merchandise;
// Ammunition and Silencers are firearm-side products: same firearm fields, validation, FFL
// shipping and no merchandise options as Firearms and Accessories
const FIREARM_CATALOG_TYPES: string[] = [
  FIREARMS_AND_ACCESSORIES_SLUG,
  AMMUNITION_CATALOG_SLUG,
  SILENCERS_CATALOG_SLUG,
];
export const isFirearmCatalogType = (catalogType: unknown) =>
  typeof catalogType === 'string' && FIREARM_CATALOG_TYPES.includes(catalogType);
// only Firearms and Accessories products have a manufacturer - not ammunition, silencers or merchandise
export const catalogTypeHasManufacturer = (catalogType: unknown) =>
  !catalogType || catalogType === FIREARMS_AND_ACCESSORIES_SLUG;
export const getCatalogTypeOptions = (t: any) => [
  { value: FIREARMS_AND_ACCESSORIES_SLUG, label: t('firearms_and_accessories') },
  { value: AMMUNITION_CATALOG_SLUG, label: t('ammunition') },
  { value: SILENCERS_CATALOG_SLUG, label: t('silencers') },
  { value: MERCHANDISE_SLUG, label: t('merchandise') },
];
export const getCatalogTypeByValue = (value: any, t: any) => {
  let selectedValue = null;
  const filteredData = getCatalogTypeOptions(t).filter(function (item) {
    return item.value == value;
  });
  if (filteredData.length > 0) {
    selectedValue = filteredData[0];
  }
  return selectedValue;
};
// category type
export const getCategoryTypeOptions = (t: any) => [
  { value: true, label: t('firearms_and_accessories') },
  { value: false, label: t('merchandise') },
];
export const getCategoryTypeByValue = (value: any, t: any) => {
  let selectedValue = null;
  const filteredData = getCategoryTypeOptions(t).filter(function (item) {
    return item.value == value;
  });
  if (filteredData.length > 0) {
    selectedValue = filteredData[0];
  }
  return selectedValue;
};
// shipping type
export const getShippingOptions = (t: any) => [
  { value: 'usps_ground_advantage', label: t('usps_ground_advantage') },
];

// shipping provider type
// Carrier display names, keyed by the carrier_slug the order carries (from the shipengine_carriers
// collection). The collection stores 'stamps.com' in lower case, so the first letter is capitalised.
// A carrier not listed yet falls back to its code with underscores read as spaces.
const CARRIER_DISPLAY_NAMES: Record<string, string> = {
  fedex: 'FedEx',
  stamps_com: 'Stamps.com',
  ups: 'UPS',
};

export const getCarrierDisplayName = (carrierCode?: string | null): string => {
  if (!carrierCode) return '';
  const code = carrierCode.trim();
  return (
    CARRIER_DISPLAY_NAMES[code.toLowerCase()] ??
    code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

export const getShippingProviderOptions = (t: any) => [{ value: 'USPS', label: t('usps') }];

// service type
export const getServiceOptions = (t: any) => [
  { value: 'Priority Mail', label: t('priority_mail') },
];

// handling type
export const getHandlingOptions = (t: any) => [
  { value: '1-3', label: `1-3 ${t('business_day')}` },
  { value: '1-5', label: `1-5 ${t('business_day')}` },
];

// get coupon obj
export const getCouponObj = (element: any, setDisabled = false) => {
  let obj = {
    id: element.id,
    code: `${element.code}`,
  };
  if (setDisabled) {
    obj = { ...obj, ...{ isDisabled: element.status ? false : true } };
  }
  return obj;
};

export const getProductStatusList = (t: any) => [
  // { id: 'inactive', name: t('inactive'), badgeBg: 'danger' },
  { id: ARCHIVED_STATUS_SLUG, name: t('archived'), badgeBg: 'danger' },
  { id: ACTIVE_STATUS_SLUG, name: t('active'), badgeBg: 'success-green' },
  { id: DRAFT_STATUS_SLUG, name: t('draft'), badgeBg: 'warning' },
  { id: 'sold', name: t('sold'), badgeBg: 'info' },
];

export const getProductStatusById = (id: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getProductStatusList(t).filter(function (item) {
    return item.id === id;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// gun for sale

export const getGunForSaleStatusList = (t: any) => [
  { id: 'inactive', name: t('inactive'), badgeBg: 'danger' },
  { id: ACTIVE_STATUS_SLUG, name: t('active'), badgeBg: 'success-green' },
  { id: DRAFT_STATUS_SLUG, name: t('draft'), badgeBg: 'warning' },
  { id: 'sold', name: t('sold'), badgeBg: 'info' },
];

export const getGunForSaleStatusById = (id: any, t: any) => {
  let selectedSlug = null;
  const filteredData = getGunForSaleStatusList(t).filter(function (item) {
    return item.id === id;
  });
  if (filteredData.length > 0) {
    selectedSlug = filteredData[0];
  }
  return selectedSlug;
};
// Which card of the FFL profile page is saving. Sent with the update so the API writes only that
// card's fields - the other cards' values stay as they are. Keep in step with FFL_PROFILE_SECTIONS
// in the API's ffl_controller.ts.
export const FFL_PROFILE_SECTION = {
  STORE_SETTINGS: 'store_settings',
  SOCIAL_LINKS: 'social_links',
  REVIEWS_LINKS: 'reviews_links',
  INTEGRATION: 'integration',
} as const;

export const cleanFFLPayload = (data: any) => {
  const payload = { ...data };

  // 1. Root Geographic Fields (serialize root objects to flat Mongo IDs)
  ['city', 'state', 'country', 'pincode'].forEach((field) => {
    if (typeof payload[field] === 'object' && payload[field] !== null) {
      payload[field] = payload[field]._id || payload[field].id;
    }
  });

  // 2. Payment Types (serialize array of objects to flat arrays of Mongo ID strings)
  if (Array.isArray(payload.payment_type)) {
    payload.payment_type = payload.payment_type.map((pt: any) => {
      if (typeof pt === 'object' && pt !== null) {
        return pt._id || pt.id;
      }
      return pt;
    });
  }

  // 3. Payment Gateways Array (serialize elements into { id, values } as expected by backend validation/saving)
  if (Array.isArray(payload.payment_gateways)) {
    payload.payment_gateways = payload.payment_gateways.map((gw: any) => {
      let pgId = gw.payment_gateway;
      if (typeof pgId === 'object' && pgId !== null) {
        pgId = pgId._id || pgId.id;
      }

      // Use resolved payment_gateway ID, falling back to stored/tracking IDs
      const gatewayId = pgId || gw.id || gw._id;

      return {
        id: gatewayId,
        values: gw.values || {},
      };
    });
  }

  // 3.5 Shipengine Carriers Array (serialize elements into { id, values } as expected by backend validation/saving)
  if (Array.isArray(payload.shipengine_carriers)) {
    payload.shipengine_carriers = payload.shipengine_carriers.map((carrier: any) => {
      let carrierId = carrier.shipengine_carrier;
      if (typeof carrierId === 'object' && carrierId !== null) {
        carrierId = carrierId._id || carrierId.id;
      }

      // Use resolved shipengine_carrier ID, falling back to stored/tracking IDs
      const resolvedId = carrierId || carrier.id || carrier._id;

      return {
        id: resolvedId,
        values: carrier.values || {},
      };
    });
  }

  // 4. Nested Address Fields (serialize populated objects within address into Raw IDs)
  if (payload.address && typeof payload.address === 'object' && !Array.isArray(payload.address)) {
    const cleanAddr = { ...payload.address };
    ['city', 'state', 'country', 'pincode'].forEach((field) => {
      if (typeof cleanAddr[field] === 'object' && cleanAddr[field] !== null) {
        cleanAddr[field] = cleanAddr[field]._id || cleanAddr[field].id;
      }
    });
    payload.address = cleanAddr;
  }

  payload.banner_image_url = [];

  return payload;
};

/**
 * Pads a number or string with leading zeros to a specific length.
 * E.g., padLeadingZeros(1, 5) -> "00001"
 */
export const padLeadingZeros = (
  num: number | string | undefined | null,
  size: number = 5
): string => {
  if (num === undefined || num === null || num === '') return '';
  return String(num).padStart(size, '0');
};

export const getFaqTypeOptions = (t: any) => [
  { value: 'general', label: t('general', 'General') },
  { value: 'sell_my_firearms', label: t('sell_my_firearms', 'Sell My Firearms') },
  { value: 'ffl_listing', label: t('ffl_listing', 'FFL Listing') },
  {
    value: 'product_listing',
    label: t('product_listing', 'Product Listing'),
  },
  {
    value: 'merchandise_product_listing',
    label: t('merchandise_product_listing', 'Merchandise Product Listing'),
  },
  {
    value: 'product_listing',
    label: t('product_listing', 'Product Listing'),
  },
];

/**
 * Formats large numbers compactly into short suffixes (e.g., 1k, 2.5k, 1m, 10m, 1b, 1t).
 */
export const formatCompactNumber = (
  amount: number | string | undefined | null,
  uppercase: boolean = false,
  spaceBeforeSuffix: boolean = false
): string => {
  if (amount === undefined || amount === null || amount === '') return '0';
  const num = typeof amount === 'number' ? amount : parseFloat(String(amount));
  if (isNaN(num) || num === 0) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  let formatted = '';
  let suffix = '';
  const sp = spaceBeforeSuffix ? ' ' : '';

  if (absNum >= 1e12) {
    formatted = (absNum / 1e12).toFixed(2).replace(/(\.00|0+)$/, '');
    suffix = sp + (uppercase ? 'T' : 't');
  } else if (absNum >= 1e9) {
    formatted = (absNum / 1e9).toFixed(2).replace(/(\.00|0+)$/, '');
    suffix = sp + (uppercase ? 'B' : 'b');
  } else if (absNum >= 1e6) {
    formatted = (absNum / 1e6).toFixed(2).replace(/(\.00|0+)$/, '');
    suffix = sp + (uppercase ? 'M' : 'm');
  } else if (absNum >= 1e3) {
    formatted = (absNum / 1e3).toFixed(2).replace(/(\.00|0+)$/, '');
    suffix = sp + (uppercase ? 'K' : 'k');
  } else {
    formatted = absNum % 1 === 0 ? absNum.toString() : absNum.toFixed(2).replace(/(\.00|0+)$/, '');
  }

  return `${sign}${formatted}${suffix}`;
};

// gun_for_sale.in_working_condition. The field name is historic - it held a boolean that
// admin and trade-in screens printed as New / Used - but it now stores the condition grade
// the seller picks.
//
// t is passed in rather than imported, matching getTargetTypeList above: building the
// labels on call keeps them out of module scope, where they would resolve before i18n
// has loaded and freeze as raw keys.
export const getGunConditionOptions = (t: any) => [
  { value: GunForSaleCondition.Excellent, label: t('excellent') },
  { value: GunForSaleCondition.Good, label: t('good') },
  { value: GunForSaleCondition.Fair, label: t('fair') },
];

// A row written before the field changed type still holds the old boolean, and it does not
// always arrive as one: the schema path is a String now, so depending on the endpoint
// Mongoose may cast it on the way out and send the string 'true' / 'false' instead. Both
// spellings mean the same thing and map the way the API migration maps them - true ->
// excellent, false -> fair - so the label is right whether or not that migration has run.
export const getGunConditionLabel = (value: any, t: any) => {
  if (value === true || value === 'true') return t('excellent');
  if (value === false || value === 'false') return t('fair');
  const grade = typeof value === 'string' ? value.toLowerCase() : value;
  return getGunConditionOptions(t).find((option) => option.value === grade)?.label || '';
};
